import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Lightbulb, Mic, Clock, Building2, Brain, AudioLines, PenTool, Briefcase, Heart, Layers, Zap, Target, Cog } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';
import { HomeHeroSection } from '@/components/HomeHeroSection';
import { HomeFeaturedSection } from '@/components/HomeFeaturedSection';
import { HomeCTASection } from '@/components/HomeCTASection';
import { HomeHCASection } from '@/components/HomeHCASection';

const systems = [
  {
    name: 'AI Means Business',
    slug: 'ai-means-business',
    tagline: 'Helping organizations apply AI today',
    icon: Briefcase,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/lb24raw7_A_I_Means_Business_R_Dale_Brunet.jpg',
    url: 'https://aimeanbusiness.net'
  },
  {
    name: 'LifeCapsule AI',
    slug: 'lifecapsule-ai',
    tagline: 'Preserving human stories and legacy',
    icon: Brain,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/rvzeh285_LCAI_LMS_Logo_Prototype_8.jpg'
  },
  {
    name: 'NewAudia',
    slug: 'newaudia',
    tagline: 'Reimagining books for the AI era',
    icon: AudioLines,
    image: '/assets/newaudia_logo_rect.png'
  },
  {
    name: 'Digital Ink AI',
    slug: 'digital-ink-ai',
    tagline: 'Tools for next-generation authors',
    icon: PenTool,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/n4rbd88f_DIGITAL_Ink_AI_Logo_Gold.jpg'
  },
  {
    name: 'The Dash Story',
    slug: 'the-dash-story',
    tagline: 'Because a life is more than a dash between dates',
    icon: Heart,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/gv1l7nv0_The_Dash_by_LifeCapsule_Left.jpg'
  },
  {
    name: 'Use What You Got Publishing',
    slug: 'use-what-you-got-publishing',
    tagline: 'Empowering authors with accessible publishing',
    icon: BookOpen,
    image: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/btrrvjk1_UWYG_Publishing_Self_Publishing_Services_Header_Logo.jpg'
  }
];

const NA = 'https://www.newaudia.com';
const books = [
  { title: 'One Million Dreams', category: 'Entrepreneurship', cover: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/utbmmmtn_NE%20MILLION%20DREAMS%20-%20Front%20Cover%20-%20LowRes.jpg' },
  { title: 'The First AI War', category: 'AI & Technology', cover: `${NA}/api/covers/45944bf6-7aea-475d-b48a-2a5ab3fbb3ca` },
  { title: 'NewAudia: The Next Chapter', category: 'Publishing', cover: 'https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/1g5ttwhg_NewAudia_The_Next_Chapter_Front_Cover.jpg' },
  { title: 'Zero Trust', category: 'Fiction', cover: `${NA}/api/covers/344db255-d77b-472c-aeb8-13f41c2c1aa9` },
  { title: 'The Unemployables', category: 'Business', cover: `${NA}/api/covers/9eb4542c-8f8c-448a-855b-1c1a4940ef56` },
  { title: 'Aligned Intelligence', category: 'AI & Technology', cover: `${NA}/api/covers/3b62215f-ab3d-4196-8cea-c7284f017e14` },
];

const ideas = [
  {
    title: 'Use What You Got and Make a Lot',
    description: 'A philosophy of practical entrepreneurship based on leveraging available resources to build meaningful ventures.',
    quote: 'Success isn\'t about having everything\u2014it\'s about maximizing what you have.'
  },
  {
    title: 'NFQ \u2014 Never FN Quit',
    description: 'A mindset emphasizing perseverance, resilience, and the willingness to continue learning and building despite obstacles.',
    quote: 'The only real failure is giving up before you\'ve explored every possibility.'
  },
  {
    title: 'Human + AI Collaboration',
    description: 'The belief that artificial intelligence should augment human creativity and insight rather than replace it.',
    quote: 'Technology should expand human capability, not diminish it.'
  }
];

export default function HomePage() {
  return (
    <main>
      <HomeHeroSection />

      <div className="relative z-10 bg-background">

      <HomeHCASection />

      <HomeFeaturedSection />

      {/* Career Credibility Bridge */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5" data-testid="credibility-bridge-heading">
              Proven Across Systems, Business, and Real-World Execution
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-3 max-w-3xl mx-auto">
              With over four decades of experience spanning enterprise systems, defense infrastructure, entrepreneurship, and artificial intelligence, I don't just study how systems work—I've built them, operated them, and relied on them in the real world.
            </p>
            <p className="text-slate-400 leading-relaxed max-w-3xl mx-auto">
              From early computing environments to mission-critical aerospace systems and modern AI platforms, my work has always centered on one thing: making complex systems practical, usable, and valuable.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { title: 'Systems Architect', desc: 'Enterprise, defense, and large-scale system design' },
              { title: 'Entrepreneur & Operator', desc: 'Built and ran real businesses, not just theory' },
              { title: 'AI Strategist', desc: 'Designing practical AI solutions for real-world use' },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.title}
                className="text-center p-5 rounded-xl bg-card/40 border border-border/20"
                initial={{ opacity: 0.5, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <h3 className="text-lg font-semibold text-white mb-2">{pillar.title}</h3>
                <p className="text-sm text-slate-400">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="explore-career-btn">
              <Link to="/career">Explore Full Career <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* The Problem Section */}
      <Section dark data-testid="problem-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">The Reality</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Technology Is Changing Everything</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0.5, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg text-slate-300 leading-relaxed mb-4">
                Businesses today face a technological paradox. Artificial intelligence promises revolutionary change, yet most organizations struggle to apply it meaningfully within their daily operations.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                Leadership sees opportunity. Staff struggles to keep existing systems running. Bridging that gap requires a new kind of advisor.
              </p>
            </motion.div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0.5, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { label: 'AI tools are evolving faster than teams can adopt them', icon: Zap },
                { label: 'Small and mid-size businesses lack internal AI expertise', icon: Target },
                { label: 'Most consulting focuses on theory, not operations', icon: Cog },
              ].map((item, i) => (
                <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 border border-border/30">
                  <item.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </Section>

      {/* The Solution Section */}
      <Section data-testid="solution-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">The Approach</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Translating Technology Into Results</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-3xl mx-auto">
              I work with organizations to identify operational inefficiencies, redesign workflows, and apply modern AI tools to automate repetitive processes and unlock new productivity. This is not about hype or theoretical technology. It's about practical systems that work today.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Brain, title: 'AI Workflow Architect', desc: 'Designing AI-powered systems for real operations' },
              { icon: Layers, title: 'Systems Thinker', desc: 'Seeing how processes connect across an organization' },
              { icon: Target, title: 'Practical AI Advisor', desc: 'Not theory\u2014actionable technology strategies' },
              { icon: Building2, title: 'Entrepreneurial Technologist', desc: 'Understanding how businesses actually operate' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="p-6 rounded-xl bg-slate-800/30 border border-border/20 text-center"
                initial={{ opacity: 0.5, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-8 inline-block"
            initial={{ opacity: 0.5 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <blockquote className="text-lg text-slate-400 italic border-l-4 border-primary pl-4 text-left max-w-xl mx-auto">
              Not a coder. Not a corporate executive. A problem translator and automation architect.
            </blockquote>
          </motion.div>
        </div>
      </Section>

      {/* Bridge to Schedule */}
      <Section dark data-testid="aimb-bridge-section">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0.5, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">Consulting</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">AI Means Business</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              Through my consultancy <strong className="text-white">AI Means Business</strong>, I help organizations evaluate where AI can create real operational value and guide them through implementation strategies that fit their existing teams and processes.
            </p>
            <p className="text-slate-400 mb-6">
              Practical AI for real businesses. Decision clarity before commitment.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="schedule-conversation-bridge-btn">
                <Link to="/schedule">Schedule a Conversation</Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="view-services-btn">
                <Link to="/schedule">View Services</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="bg-slate-900 rounded-2xl border border-border/30 overflow-hidden"
            initial={{ opacity: 0.5, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/lb24raw7_A_I_Means_Business_R_Dale_Brunet.jpg"
              alt="AI Means Business"
              className="w-1/2 mx-auto aspect-square object-contain p-6"
            />
          </motion.div>
        </div>
      </Section>

      {/* The Systems I'm Building */}
      <Section data-testid="ventures-section">
        <SectionTitle subtitle="Ecosystem">The Systems I'm Building</SectionTitle>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {systems.map((s, index) => (
            <AnimatedCard key={s.name} delay={index * 0.08} className="group overflow-hidden">
              <div className="h-36 -mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-5 bg-slate-900 overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-full object-contain p-4" />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <s.icon className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-white">{s.name}</h3>
              </div>
              <p className="text-muted-foreground text-sm mb-3">{s.tagline}</p>
              <Link
                to={`/ventures#${s.slug}`}
                className="text-primary text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                data-testid={`venture-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </AnimatedCard>
          ))}
        </div>
      </Section>

      {/* Singular Vision Teaser */}
      <Section dark data-testid="singular-vision-teaser">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">Singular Vision</span>
            <blockquote className="text-2xl md:text-3xl text-white font-semibold leading-snug mb-5" data-testid="vision-teaser-quote">
              To empower people to use what they've got to create something extraordinary.
            </blockquote>
            <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-2xl mx-auto">
              My work sits at the intersection of technology, publishing, entrepreneurship, and legacy—focused on helping people build, adapt, and preserve what matters most.
            </p>
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="read-full-vision-btn">
              <Link to="/about#singular-vision">Read the Full Vision <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* Credibility: Books & Writing */}
      <Section dark data-testid="books-section">
        <SectionTitle subtitle="Depth of Thinking">Books & Writing</SectionTitle>
        <p className="text-slate-400 mb-6 max-w-2xl -mt-4">
          40+ published titles across AI, entrepreneurship, fiction, and technology. Each book reflects practical wisdom gained from decades of building.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {books.map((book, index) => (
            <motion.div
              key={book.title}
              className="group cursor-pointer"
              initial={{ opacity: 0.5, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              whileHover={{ y: -6 }}
            >
              <div className="aspect-[2/3] rounded-lg mb-2 overflow-hidden bg-slate-800">
                <img src={book.cover} alt={book.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <span className="text-sm font-semibold text-white leading-tight line-clamp-1">{book.title}</span>
              <span className="text-xs text-muted-foreground block">{book.category}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0.5 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="view-library-btn">
            <Link to="/books">View Complete Library <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </motion.div>
      </Section>

      {/* Ideas & Philosophy */}
      <Section data-testid="ideas-section">
        <SectionTitle subtitle="Philosophy">Ideas & Principles</SectionTitle>

        <div className="grid md:grid-cols-3 gap-6">
          {ideas.map((idea, index) => (
            <AnimatedCard key={idea.title} delay={index * 0.1} className="flex flex-col">
              <Lightbulb className="w-7 h-7 text-primary mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">{idea.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 flex-grow">{idea.description}</p>
              <blockquote className="border-l-2 border-primary pl-3 text-sm text-slate-300 italic">
                "{idea.quote}"
              </blockquote>
            </AnimatedCard>
          ))}
        </div>

        <motion.div
          className="mt-8 text-center flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0.5 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="explore-ideas-btn">
            <Link to="/ideas">Explore All Ideas <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="read-insights-btn">
            <Link to="/insights">Read Insights <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </motion.div>
      </Section>

      {/* Speaking */}
      <Section dark data-testid="speaking-section">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <SectionTitle subtitle="Speaking">Thought Leadership</SectionTitle>

            <div className="space-y-3 mb-6">
              {[
                'Entrepreneur Groups & Accelerators',
                'Technology Conferences & Industry Events',
                'Chambers of Commerce & Business Organizations',
                'AI Adoption & Operational Transformation'
              ].map((topic, index) => (
                <motion.div
                  key={topic}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0.5, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                >
                  <Mic className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-slate-300">{topic}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="book-speaking-btn">
                <Link to="/speaking">Book a Speaking Engagement</Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="request-consultation-btn">
                <Link to="/contact">Request Consultation</Link>
              </Button>
            </div>
          </div>

          <motion.div
            className="relative"
            initial={{ opacity: 0.5, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/c45c2fgp_R_Dale_Brunet_AI_3.jpg"
              alt="R. Dale Brunet speaking"
              className="rounded-xl w-full aspect-square object-cover object-top"
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-background/50 to-transparent" />
          </motion.div>
        </div>
      </Section>

      <HomeCTASection />
      </div>
    </main>
  );
}
